import { FlagIcon } from "@phosphor-icons/react/dist/csr/Flag";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@volca/ui/components/ui/alert";
import { splitFlag } from "@volca/ui/trades";
import { shortPeriod } from "@/profitability/new-run/run-period";
import { FindingStatusMenu } from "@/profitability/review/finding-status";
import { causeName, nextStepName } from "@/profitability/review/labels";
import { Rating } from "@/profitability/review/rating";
import { useReviewSpec } from "@/profitability/review/review-spec";
import type { Finding } from "@/profitability/review/types";

/**
 * What the report flagged about the record, as a banner the way a missing
 * file is one, marked with the flag the queue uses: the rule and the period
 * it was flagged in, what it found, and Volca's guess and next step when it
 * made one. The record's rating and status sit under it, as its controls.
 */
export const RecordFinding = ({
  businessId,
  finding,
  label,
  noun,
}: {
  businessId: string;
  finding: Finding;
  label: string;
  /** What the record is, lower case: "job". */
  noun?: string;
}) => {
  const spec = useReviewSpec();
  // The rule is the API's (`finding.rule`); the flag is split only to show
  // its own words after the colon.
  const { detail } = splitFlag(finding.flag);
  const note = detail || finding.flag;
  const hypothesis = finding.hypothesis;
  const step = hypothesis?.action
    ? nextStepName(hypothesis.action, hypothesis.action_label, spec.next_steps)
    : hypothesis?.action_label;
  return (
    <section aria-label="Flagged" className="flex flex-col gap-2">
      <Alert color="warning">
        <FlagIcon aria-hidden />
        <AlertTitle>
          {finding.rule}
          {finding.start_date && finding.end_date && (
            <span className="ms-1.5 font-normal text-subtle tabular-nums">
              {shortPeriod({
                start_date: finding.start_date,
                end_date: finding.end_date,
              })}
            </span>
          )}
        </AlertTitle>
        <AlertDescription>
          <span>{note.charAt(0).toUpperCase() + note.slice(1)}</span>
          {hypothesis?.cause && (
            <dl className="mt-1 grid grid-cols-[6rem_minmax(0,1fr)] gap-x-3 gap-y-1">
              <dt className="text-subtle">Likely cause</dt>
              <dd className="text-ink">
                {causeName(
                  hypothesis.cause,
                  hypothesis.cause_name,
                  spec.causes,
                )}
              </dd>
              {step && (
                <>
                  <dt className="text-subtle">Next step</dt>
                  <dd className="text-ink">{step}</dd>
                </>
              )}
            </dl>
          )}
        </AlertDescription>
      </Alert>
      <div className="-mx-1.5 flex items-center justify-between gap-2">
        <Rating
          businessId={businessId}
          subjectType="finding"
          subjectId={finding.id}
          runId={finding.run_id}
          feedback={finding.feedback}
          label={label}
          noun={noun}
        />
        <FindingStatusMenu businessId={businessId} finding={finding} />
      </div>
    </section>
  );
};
